import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type { Opcion, Pagina, Refaccion } from '../api/tipos';

/**
 * Elegir una refacción entre cuatrocientas.
 *
 * Un desplegable plano con todo el catálogo es inservible: nadie recuerda
 * el código y el nombre no basta para distinguir dos piezas parecidas.
 * Aquí se acota igual que en el alta de la pieza —área, equipo, marca,
 * modelo— y además se busca por texto, porque quien tiene el número de
 * parte en la mano no quiere recorrer cuatro cascadas.
 *
 * Los filtros de equipo, marca y modelo no consultan la refacción sino su
 * matriz de compatibilidad: responden a «qué piezas sirven para esta
 * máquina», que es la pregunta con la que llega el técnico.
 *
 * Las opciones se cargan una vez y se comparten; el resultado se pide a
 * la API con un respiro de 300 ms para no consultar en cada tecla.
 */
export function BuscadorRefaccion({
  valor, onElegir, autoFoco, id = 'buscarRefaccion',
}: {
  valor: Refaccion | null;
  onElegir: (refaccion: Refaccion | null) => void;
  autoFoco?: boolean;
  /**
   * Para cuando hay más de uno en la misma pantalla.
   *
   * La cotización puede llevar varias piezas y cada renglón trae su
   * buscador. Con el id fijo, todos los `label` apuntaban al primero:
   * hacer clic en la etiqueta del tercer renglón movía el foco al de
   * arriba, y el lector de pantalla leía siempre el mismo campo.
   */
  id?: string;
}) {
  const [areas, setAreas] = useState<Opcion[]>([]);
  const [tiposEquipo, setTiposEquipo] = useState<Opcion[]>([]);
  const [marcas, setMarcas] = useState<Opcion[]>([]);
  const [modelos, setModelos] = useState<Opcion[]>([]);

  const [filtros, setFiltros] = useState({
    busqueda: '', areaId: '', tipoEquipoId: '', marcaId: '', modeloId: '',
  });
  const [resultados, setResultados] = useState<Refaccion[]>([]);
  const [buscando, setBuscando] = useState(false);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/areas/opciones').then(setAreas).catch(() => undefined);
    api.get<Opcion[]>('/catalogos/tipos-equipo/opciones').then(setTiposEquipo).catch(() => undefined);
    api.get<Opcion[]>('/catalogos/marcas/opciones').then(setMarcas).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!filtros.marcaId) { setModelos([]); return; }
    api.get<Opcion[]>('/catalogos/modelos/opciones', { marca_id: filtros.marcaId })
      .then(setModelos)
      .catch(() => setModelos([]));
  }, [filtros.marcaId]);

  const hayFiltro = Object.values(filtros).some(Boolean);

  const buscar = useCallback(() => {
    // Sin ningún filtro no se trae el catálogo entero: cuatrocientos
    // renglones sin criterio no son una lista, son un muro.
    if (!hayFiltro) { setResultados([]); return; }

    setBuscando(true);
    api.get<Pagina<Refaccion>>('/refacciones', {
      busqueda: filtros.busqueda || undefined,
      areaId: filtros.areaId || undefined,
      tipoEquipoId: filtros.tipoEquipoId || undefined,
      marcaId: filtros.marcaId || undefined,
      modeloId: filtros.modeloId || undefined,
      estado: 'activo',
      limite: 25,
    })
      .then((p) => setResultados(p.data))
      .catch(() => setResultados([]))
      .finally(() => setBuscando(false));
  }, [filtros, hayFiltro]);

  useEffect(() => {
    const t = setTimeout(buscar, filtros.busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [buscar, filtros.busqueda]);

  function cambiar(parcial: Partial<typeof filtros>) {
    setFiltros((f) => ({ ...f, ...parcial }));
  }

  // Ya elegida: se enseña cuál es y se ofrece cambiarla. Dejar los
  // filtros abiertos después de elegir invita a elegir dos veces.
  if (valor) {
    return (
      <div className="campo">
        <label>Refacción</label>
        <p>
          <strong className="mono">{valor.codigo}</strong> — {valor.nombre}
          {valor.numeroParte && <span className="conteo"> · {valor.numeroParte}</span>}
        </p>
        <button className="enlace" onClick={() => onElegir(null)}>Cambiar</button>
      </div>
    );
  }

  return (
    <div className="campo">
      <label htmlFor={id}>Refacción</label>

      <input
        id={id}
        type="search"
        autoFocus={autoFoco}
        placeholder="Código, número de parte o nombre…"
        value={filtros.busqueda}
        onChange={(e) => cambiar({ busqueda: e.target.value })}
      />

      <div className="controles" style={{ marginTop: 8 }}>
        <select value={filtros.areaId} onChange={(e) => cambiar({ areaId: e.target.value })}>
          <option value="">Todas las áreas</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>

        <select
          value={filtros.tipoEquipoId}
          onChange={(e) => cambiar({ tipoEquipoId: e.target.value })}
        >
          <option value="">Todos los equipos</option>
          {tiposEquipo.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
        </select>

        <select
          value={filtros.marcaId}
          onChange={(e) => cambiar({ marcaId: e.target.value, modeloId: '' })}
        >
          <option value="">Todas las marcas</option>
          {marcas.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
        </select>

        <select
          value={filtros.modeloId}
          disabled={!filtros.marcaId}
          onChange={(e) => cambiar({ modeloId: e.target.value })}
        >
          <option value="">{filtros.marcaId ? 'Todos los modelos' : 'Elige una marca'}</option>
          {modelos.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
        </select>
      </div>

      {!hayFiltro ? (
        <span className="ayuda">
          Busca por texto o acota por área, equipo, marca y modelo. El
          catálogo tiene más de cuatrocientas piezas.
        </span>
      ) : buscando ? (
        <span className="ayuda">Buscando…</span>
      ) : resultados.length === 0 ? (
        <span className="ayuda">
          Ninguna pieza cumple eso. Si no está en el catálogo, descríbela
          abajo y almacén la identifica.
        </span>
      ) : (
        <table>
          <thead>
            {/* El número de parte en su columna y no debajo del nombre:
                es el dato con el que se compara contra el catálogo del
                proveedor, y comparar una columna es leer hacia abajo. */}
            <tr>
              <th>Código</th>
              <th>Nº de parte</th>
              <th>Nombre</th>
              <th>Área</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {resultados.map((r) => (
              <tr key={r.id}>
                <td className="mono">{r.codigo}</td>
                <td className="mono">{r.numeroParte ?? '—'}</td>
                <td>{r.nombre}</td>
                <td>{r.area?.nombre ?? '—'}</td>
                <td>
                  <button className="enlace" onClick={() => onElegir(r)}>Elegir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
